import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  FileText,
  Edit,
  Trash2,
  Volume2,
  MessageSquare,
  MessageCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentDetailPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);

  const fetchAppointment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointment(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching appointment:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await axios.get(`${API}/availability/slots/${date}`);
      // Include current appointment time as available
      const slots = response.data.slots.map(s => ({
        ...s,
        available: s.available || s.time === appointment?.appointment_time
      }));
      setAvailableSlots(slots.filter(s => s.available));
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleDateChange = (date) => {
    setEditData({ ...editData, appointment_date: date, appointment_time: '' });
    if (date) fetchAvailableSlots(date);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/appointments/${id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointment({ ...appointment, status: newStatus });
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/appointments/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEdit(false);
      fetchAppointment();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar cita');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta cita?')) return;
    
    try {
      await axios.delete(`${API}/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/admin/appointments');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar cita');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      attended: 'status-attended',
      cancelled: 'status-cancelled',
      no_show: 'status-no_show'
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      attended: 'Atendida',
      cancelled: 'Cancelada',
      no_show: 'No asistió'
    };
    return <Badge className={`${styles[status]} text-sm px-3 py-1`}>{labels[status]}</Badge>;
  };

  const handleSendWhatsAppReminder = () => {
    if (!appointment.patient_phone) {
      alert('El paciente no tiene número de teléfono registrado');
      return;
    }
    
    // Format date nicely
    const appointmentDate = new Date(appointment.appointment_date + 'T12:00:00');
    const formattedDate = format(appointmentDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
    
    // Build the reminder message
    const message = `¡Hola ${appointment.patient_name}! 👋

Le recordamos su cita con el *Dr. Cristian Acevedo - Oftalmólogo*:

📅 *Fecha:* ${formattedDate}
🕐 *Hora:* ${appointment.appointment_time}
${appointment.reason_text ? `📋 *Motivo:* ${appointment.reason_text}` : ''}

📍 *Dirección:* [Agregar dirección de la clínica]

Por favor confirme su asistencia respondiendo a este mensaje.

¡Gracias por su preferencia!`;

    // Clean phone number and create WhatsApp link
    const cleanPhone = appointment.patient_phone.replace(/[^0-9]/g, '');
    const phoneWithCountry = cleanPhone.startsWith('502') ? cleanPhone : `502${cleanPhone}`;
    const whatsappUrl = `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Cita no encontrada</p>
        <Button variant="link" onClick={() => navigate('/admin/appointments')}>
          Volver a citas
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointment-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/appointments')}
          className="p-2"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Detalle de Cita</h1>
          <p className="text-slate-500 mt-1">ID: {appointment.id.slice(0, 8)}...</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={handleSendWhatsAppReminder} 
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            data-testid="whatsapp-reminder-button"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Recordatorio WhatsApp
          </Button>
          <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-button">
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="destructive" onClick={handleDelete} data-testid="delete-button">
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Card */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-[#0891B2]" />
                Información del Paciente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Nombre</p>
                  <p className="font-semibold text-lg">{appointment.patient_name}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Teléfono</p>
                  <p className="font-semibold text-lg flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {appointment.patient_phone}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Appointment Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#0891B2]" />
                Detalles de la Cita
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Fecha</p>
                  <p className="font-semibold text-lg">
                    {format(new Date(appointment.appointment_date + 'T12:00:00'), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Hora</p>
                  <p className="font-semibold text-lg flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {appointment.appointment_time}
                  </p>
                </div>
              </div>

              {appointment.reason_text && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Motivo de consulta
                  </p>
                  <p className="text-slate-700">{appointment.reason_text}</p>
                </div>
              )}

              {appointment.reason_transcription && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Transcripción del audio
                  </p>
                  <p className="text-slate-700">{appointment.reason_transcription}</p>
                </div>
              )}

              {appointment.reason_audio_url && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                    <Volume2 className="h-4 w-4" />
                    Audio del motivo
                  </p>
                  <audio controls className="w-full" data-testid="audio-player">
                    <source src={appointment.reason_audio_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}

              {appointment.internal_notes && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 mb-2 font-medium">Notas internas</p>
                  <p className="text-slate-700">{appointment.internal_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Estado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center mb-4">
                {getStatusBadge(appointment.status)}
              </div>
              <Select 
                value={appointment.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Cambiar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="confirmed">Confirmada</SelectItem>
                  <SelectItem value="attended">Atendida</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                  <SelectItem value="no_show">No asistió</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Meta Info */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Origen</span>
                <Badge variant="outline">
                  {appointment.source === 'manual' ? 'Manual' : 
                   appointment.source === 'voice' ? 'Voz' : 'Web'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Creada</span>
                <span>{format(new Date(appointment.created_at), "d MMM yyyy, HH:mm", { locale: es })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Actualizada</span>
                <span>{format(new Date(appointment.updated_at), "d MMM yyyy, HH:mm", { locale: es })}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71]">Editar Cita</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del paciente</Label>
              <Input
                value={editData.patient_name || ''}
                onChange={(e) => setEditData({ ...editData, patient_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={editData.patient_phone || ''}
                onChange={(e) => setEditData({ ...editData, patient_phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={editData.appointment_date || ''}
                onChange={(e) => handleDateChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hora</Label>
              <Select 
                value={editData.appointment_time || ''}
                onValueChange={(value) => setEditData({ ...editData, appointment_time: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona hora" />
                </SelectTrigger>
                <SelectContent>
                  {availableSlots.map((slot) => (
                    <SelectItem key={slot.time} value={slot.time}>{slot.time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea
                value={editData.reason_text || ''}
                onChange={(e) => setEditData({ ...editData, reason_text: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                value={editData.internal_notes || ''}
                onChange={(e) => setEditData({ ...editData, internal_notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setShowEdit(false)}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-[#0891B2] hover:bg-[#0e7490]" onClick={handleUpdate}>
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentDetailPage;
